import * as THREE from "three";
import { ARENA, OBSTACLES, SPAWN_POINTS, terrainHeight } from "@tank/shared";

/**
 * Decorative low-poly bush clusters — purely visual (never in `OBSTACLES`, so
 * never collidable; same convention as GrassField). Placed with INTENT rather
 * than uniform random scatter across the whole floor: clustered just outside
 * natural/fortified cover (rocks, sandbags, crates — skips the plain "wall"
 * building segments, which get their own detailing elsewhere) so they read as
 * ground cover that grew up around those features, plus a light, gapped
 * scatter hugging the inside of the perimeter wall so the boundary reads as a
 * tree/hedge line instead of bare grass right up to the fence. Both placement
 * passes stay clear of spawn points so nobody's spawn is boxed in.
 */

function seededRandom(seed: number): () => number {
  let s = Math.abs(Math.round(seed)) + 1;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return (s % 1000) / 1000;
  };
}

const BUSH_TINTS = [0x3f6b2e, 0x4a7a35, 0x38602a];
const bushMats = BUSH_TINTS.map((color) => new THREE.MeshStandardMaterial({ color, roughness: 1, flatShading: true }));
const stemMat = new THREE.MeshStandardMaterial({ color: 0x5b4630, roughness: 0.95 });
// Shared across every blob/stem — only per-instance transform (scale/position/
// rotation) varies, so this stays cheap regardless of bush count.
const foliageGeo = new THREE.IcosahedronGeometry(1, 0);
const stemGeo = new THREE.CylinderGeometry(0.08, 0.11, 1, 6);

/** A single shrub: a short stem + 3-4 overlapping foliage blobs of varying
 * size/tint/rotation, so it reads as a real bush rather than one plain sphere. */
function buildBush(seed: number): THREE.Object3D {
  const rand = seededRandom(seed);
  const g = new THREE.Group();
  const stemH = 0.18 + rand() * 0.1;
  const stem = new THREE.Mesh(stemGeo, stemMat);
  stem.scale.set(1, stemH, 1);
  stem.position.y = stemH / 2;
  g.add(stem);
  const blobs = 3 + Math.floor(rand() * 2);
  for (let i = 0; i < blobs; i++) {
    const mat = bushMats[Math.floor(rand() * bushMats.length)];
    const blob = new THREE.Mesh(foliageGeo, mat);
    const r = 0.32 + rand() * 0.22;
    blob.scale.setScalar(r);
    const a = (i / blobs) * Math.PI * 2 + rand();
    const d = i === 0 ? 0 : 0.14 + rand() * 0.12;
    blob.position.set(Math.cos(a) * d, stemH + r * 0.6, Math.sin(a) * d);
    blob.rotation.set(rand() * Math.PI, rand() * Math.PI, rand() * Math.PI);
    g.add(blob);
  }
  return g;
}

function isClearOfSpawns(x: number, z: number, minDist = 8): boolean {
  for (const s of SPAWN_POINTS) {
    if (Math.hypot(x - s.x, z - s.z) < minDist) return false;
  }
  return true;
}

export function buildVegetation(scene: THREE.Scene): void {
  const placements: Array<{ x: number; z: number; seed: number }> = [];

  // Ground cover just outside natural/fortified obstacles.
  for (const o of OBSTACLES) {
    if (o.kind === "wall") continue;
    const rand = seededRandom(o.x * 13 + o.z * 7);
    const clusterCount = 1 + Math.floor(rand() * 2);
    const radius = Math.hypot(o.w, o.d) / 2 + 1.4;
    for (let i = 0; i < clusterCount; i++) {
      const a = rand() * Math.PI * 2;
      const d = radius + rand() * 1.5;
      const x = o.x + Math.cos(a) * d;
      const z = o.z + Math.sin(a) * d;
      if (Math.abs(x) > ARENA.HALF - 2 || Math.abs(z) > ARENA.HALF - 2) continue;
      if (!isClearOfSpawns(x, z)) continue;
      placements.push({ x, z, seed: x * 31 + z * 17 + i });
    }
  }

  // A light, gapped scatter along the inside of the perimeter wall.
  const H = ARENA.HALF;
  const inset = 3.5;
  const spacing = 12;
  for (let side = 0; side < 4; side++) {
    for (let t = -H + spacing; t < H - spacing; t += spacing) {
      const rand = seededRandom(side * 971 + t * 3.3);
      if (rand() < 0.35) continue; // gaps so it doesn't read as a solid hedge wall
      const jitter = (rand() - 0.5) * 3;
      let x = 0;
      let z = 0;
      if (side === 0) { x = t + jitter; z = -H + inset; }
      else if (side === 1) { x = t + jitter; z = H - inset; }
      else if (side === 2) { x = -H + inset; z = t + jitter; }
      else { x = H - inset; z = t + jitter; }
      if (!isClearOfSpawns(x, z)) continue;
      placements.push({ x, z, seed: x * 41 + z * 23 });
    }
  }

  for (const p of placements) {
    const bush = buildBush(p.seed);
    bush.position.set(p.x, terrainHeight(p.x, p.z), p.z);
    bush.rotation.y = seededRandom(p.seed + 99)() * Math.PI * 2;
    bush.traverse((n) => {
      if (n instanceof THREE.Mesh) {
        n.castShadow = true;
        n.receiveShadow = true;
      }
    });
    scene.add(bush);
  }
}
